import { ChevronRight } from 'lucide-react';
import { Button, GlassCard, Pill, type PillProps } from '@/design-system';
import { cn } from '@/shared/cn';
import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { formatCaseClock } from '@/versions/v1/shared/clock.derive';
import { ladderLineForCase } from '@/versions/v1/shared/ladder.transform';
import { MeterBar, vizToneColor } from '@/versions/v1/modules/_viz';
import { SeverityChip } from './SeverityChip';
import { severityPillTone } from './RoleScreen.transform';
import { STAGE_ROW_BUTTON, STAGE_ROW_TITLE, statePillClass } from './stage-body-ui';
import {
  confidenceTone,
  priorityFromSeverity,
  severityChip,
  URGENCY_LABEL,
  urgencyTone,
  variationEventForCase,
} from './Scene.transform';

export type FindingCardStatusChip = {
  label: string;
  tone: PillProps['tone'];
  title?: string;
};

export type FindingCardProps = {
  caseItem: Case;
  actions: readonly RecommendedAction[];
  variant?: 'full' | 'compact';
  /** Primary-list row (~56–72px): title · meta · observation · status chips · Open case. */
  compact?: boolean;
  titleOverride?: string;
  /** Replaces the observation line — used for recently-closed rows. */
  detailOverride?: string;
  /** Upstream change + correlation nudge (#326 / #324). */
  correlationLine?: string;
  statusChips?: readonly FindingCardStatusChip[];
  onOpen?: () => void;
  canApprove?: boolean;
  onApproveAction?: (id: string) => void;
  className?: string;
  cursor?: SeriesTime;
};

function detectedAtLabel(caseItem: Case): string {
  const variation = variationEventForCase(caseItem);
  const stamp = variation?.detectedAt ?? caseItem.raisedAt;
  return formatCaseClock(stamp, DEFAULT_CURSOR);
}

function compactMetaLine(caseItem: Case): string {
  const parts: string[] = [];
  if (caseItem.code) parts.push(caseItem.code);
  parts.push(caseItem.circuit);
  parts.push(`detected ${detectedAtLabel(caseItem)}`);
  return parts.join(' · ');
}

/** Status walls are quiet (polish.P28b): a ladder inside its clock reads ink;
 *  only a ladder near or past its limit takes its state colour. */
function ladderLineColor(tone: 'ok' | 'warn' | 'bad'): string {
  return tone === 'ok' ? 'var(--ink-secondary)' : vizToneColor(tone);
}

export function FindingCard({
  caseItem,
  actions,
  variant = 'full',
  compact = false,
  titleOverride,
  detailOverride,
  correlationLine,
  statusChips = [],
  onOpen,
  canApprove = false,
  onApproveAction,
  className,
  cursor = DEFAULT_CURSOR,
}: FindingCardProps) {
  const chip = severityChip(caseItem);
  const borderColor = vizToneColor(chip.tone);
  const priority = priorityFromSeverity(caseItem.severity);
  const headline = caseItem.headline;
  const confidence = caseItem.aiConfidencePct;
  const displayTitle = titleOverride ?? caseItem.title;
  const ladderLine = ladderLineForCase(caseItem, cursor);

  if (compact) {
    const ladderClockLine =
      ladderLine
        ? [ladderLine.instruction, ladderLine.clock].filter(Boolean).join(' · ')
        : null;
    return (
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className={cn(
          STAGE_ROW_BUTTON,
          'flex items-start gap-3 px-4 py-2',
          !onOpen && 'cursor-default hover:bg-transparent',
          className,
        )}
      >
        <div className="min-w-0 flex-1 space-y-0.5">
          {/* fix-p28b-r1 item 8: the title and the observation read whole (they
              wrap, never truncate); the context lines under them stay one line. */}
          <div className="flex min-w-0 items-start gap-2">
            <p className={cn(STAGE_ROW_TITLE, 'min-w-0 break-words')} data-finding-title>
              {displayTitle}
            </p>
            <Pill tone={severityPillTone(caseItem.severity)} className={cn('shrink-0', statePillClass(severityPillTone(caseItem.severity)))}>{chip.label}</Pill>
          </div>
          <p className="truncate font-mono text-[10px] text-muted">{compactMetaLine(caseItem)}</p>
          {ladderLine && ladderClockLine ? (
            <p
              className="line-clamp-1 text-xs font-medium"
              style={{ color: ladderLineColor(ladderLine.tone) }}
            >
              {ladderClockLine}
            </p>
          ) : null}
          {correlationLine ? (
            <p className="line-clamp-1 text-xs text-muted">{correlationLine}</p>
          ) : null}
          <p className="text-xs text-ink-secondary" data-finding-observation>{detailOverride ?? caseItem.observation}</p>
        </div>
        {statusChips.length > 0 ? (
          <div className="flex max-w-[45%] shrink-0 flex-wrap items-center justify-end gap-1 self-center">
            {statusChips.map((statusChip) => (
              <Pill key={statusChip.label} tone={statusChip.tone} title={statusChip.title} className={statePillClass(statusChip.tone)}>
                {statusChip.label}
              </Pill>
            ))}
          </div>
        ) : null}
        {onOpen ? <ChevronRight className="size-4 shrink-0 self-center text-muted" aria-hidden /> : null}
      </button>
    );
  }

  return (
    <GlassCard
      className={cn('border-l-[3px] border-t-[3px] p-4', className)}
      style={{ borderLeftColor: borderColor, borderTopColor: borderColor }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{caseItem.title}</p>
          {caseItem.code ? (
            <p className="font-mono text-xs text-muted">
              {caseItem.code} · {priority}
            </p>
          ) : null}
        </div>
        <SeverityChip label={chip.label} tone={chip.tone} />
      </div>

      {headline ? (
        <p className="mt-2 text-xs text-zinc-900 dark:text-zinc-100">
          <span className="text-muted">{headline.label}:</span>{' '}
          <span className="font-medium tabular-nums">{headline.value}</span>
          {headline.basis ? (
            <span className="text-muted">
              {' '}
              ({headline.basis.label}: {headline.basis.value})
            </span>
          ) : null}
          <span
            className="ml-1 font-medium tabular-nums"
            style={{ color: vizToneColor(headline.delta.tone) }}
          >
            {headline.delta.direction === 'down' ? '▼' : headline.delta.direction === 'up' ? '▲' : '—'}{' '}
            {headline.delta.text}
          </span>
        </p>
      ) : null}

      {ladderLine ? (
        <p
          className="mt-2 text-xs font-medium leading-relaxed"
          style={{ color: ladderLineColor(ladderLine.tone) }}
        >
          {ladderLine.text}
        </p>
      ) : null}

      {variant === 'compact' ? (
        <p className="mt-2 text-xs leading-relaxed text-muted">{caseItem.observation}</p>
      ) : (
        <>
          <p className="mt-2 text-xs leading-relaxed text-muted">{caseItem.observation}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted">{caseItem.causeCandidate}</p>
        </>
      )}

      {confidence !== undefined ? (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">AI confidence</p>
          <MeterBar
            value={confidence}
            max={100}
            tone={confidenceTone(confidence)}
            valueLabel={`${confidence}%`}
          />
          {caseItem.confidenceDiscount ? (
            <p className="text-xs text-muted">
              {caseItem.confidenceDiscount.reason}
            </p>
          ) : null}
        </div>
      ) : null}

      {variant === 'full' && actions.length > 0 ? (
        <ol className="mt-3 space-y-1.5">
          {actions.map((action, index) => (
            <li key={action.id} className="flex items-center gap-2 text-xs">
              <span className="w-3 shrink-0 tabular-nums text-muted">{index + 1}</span>
              <SeverityChip
                label={URGENCY_LABEL[action.urgency]}
                tone={urgencyTone(action.urgency)}
                solid
              />
              <p className="min-w-0 flex-1 text-muted">{action.text}</p>
              {canApprove && action.status === 'proposed' && onApproveAction ? (
                <Button variant="ghost" className="shrink-0 text-xs" onClick={() => onApproveAction(action.id)}>
                  Approve
                </Button>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}

      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="mt-3 text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
        >
          Open case →
        </button>
      ) : null}
    </GlassCard>
  );
}
